import React, { useState, useEffect, useRef } from 'react';
import { Plus, ChevronDown } from 'lucide-react';

interface AutoCompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  options: string[];
  loading?: boolean;
  className?: string;
}

const AutoComplete: React.FC<AutoCompleteProps> = ({
  value,
  onChange,
  placeholder,
  options,
  loading = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<string[]>([]);
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter options based on input
  useEffect(() => {
    if (value) {
      const filtered = options.filter(option =>
        option.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredOptions(filtered);

      // Show "create new" if no exact match and value is not empty
      const exactMatch = options.some(option =>
        option.toLowerCase() === value.toLowerCase()
      );
      setShowCreateNew(!exactMatch && value.trim() !== '');
    } else {
      setFilteredOptions(options);
      setShowCreateNew(false);
    }
    setSelectedIndex(-1);
  }, [value, options]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => {
          const maxIndex = filteredOptions.length + (showCreateNew ? 1 : 0) - 1;
          return Math.min(prev + 1, maxIndex);
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredOptions.length) {
          onChange(filteredOptions[selectedIndex]);
          setIsOpen(false);
        } else if (selectedIndex === filteredOptions.length && showCreateNew) {
          onChange(value);
          setIsOpen(false);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleOptionClick = (option: string) => {
    onChange(option);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleCreateNew = () => {
    // Format the name when creating new entry
    const formatted = value
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    onChange(formatted);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  // const formatPublisherName = (name: string) => {
  //   // Only format when the user finishes typing (on blur or when creating new)
  //   // Don't format while typing to allow spaces and natural input
  //   return name;
  // };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow natural typing without auto-formatting
    onChange(e.target.value);
  };

  const handleInputBlur = () => {
    // Format the name when user finishes typing
    if (value.trim()) {
      const formatted = value
        .trim()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      onChange(formatted);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {loading ? (
            <div className="p-3 text-center text-gray-500 dark:text-gray-400">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
              <span className="ml-2">Loading {placeholder}...</span>
            </div>
          ) : (
            <>
              {/* Existing options */}
              {filteredOptions.map((option, index) => (
                <div
                  key={index}
                  className={`px-3 py-2 cursor-pointer transition-colors ${selectedIndex === index
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`}
                  onClick={() => handleOptionClick(option)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  {option}
                </div>
              ))}

              {/* Create New Option */}
              {showCreateNew && (
                <div className="border-t border-gray-200 dark:border-gray-600">
                  <div
                    className={`px-3 py-2 cursor-pointer transition-colors flex items-center space-x-2 ${selectedIndex === filteredOptions.length
                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100'
                      : 'hover:bg-blue-50 dark:hover:bg-blue-900 text-blue-600 dark:text-blue-400'
                      }`}
                    onClick={handleCreateNew}
                    onMouseEnter={() => setSelectedIndex(filteredOptions.length)}
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add "{value}" as new {placeholder}</span>
                  </div>
                </div>
              )}

              {/* No results */}
              {filteredOptions.length === 0 && !showCreateNew && (
                <div className="p-3 text-gray-500 dark:text-gray-400 text-center">
                  No {placeholder} found
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AutoComplete;
